import type { User } from '@/types/user';
import { supabase } from '@/lib/supabase';
import { profilesService } from '@/lib/supabase/profiles';
import { getErrorMessage, handleSupabaseError, logError } from '@/utils/error-handling';
import { normalizeEmail } from '@/utils/formatting';
import { organizationsService } from '@/lib/supabase/organizer/organizations';
import { hasAdminAccess as checkHasAdminAccess, hasSuperAdminAccess as checkHasSuperAdminAccess, isAdmin as checkIsAdmin } from '@/utils/role-helpers';
import { generateAvatar, generateNameFromEmail } from '@/utils/user-helpers';
import { validateEmail, validatePassword } from '@/utils/validation';
import { isAuthWeakPasswordError } from '@supabase/auth-js';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

interface AuthUser {
  email: string;
  password: string;
  createdAt: string;
}

interface RegisteredUser extends User {
  password: string; // Solo para autenticación, no se expone en usersDB
}

interface RegisterResult {
  success: boolean;
  error?: string;
  requiresEmailConfirmation?: boolean;
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface PasswordResetResult {
  success: boolean;
  error?: string;
}

const getPasswordResetRedirectUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/reset-password`;
  }
  return 'https://biavent.com/reset-password';
};

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isPasswordRecovery: boolean;
  currentUser: string | null;
  currentUserProfile: User | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (email: string, password: string, name?: string) => Promise<RegisterResult>;
  requestPasswordReset: (email: string) => Promise<PasswordResetResult>;
  completePasswordReset: (newPassword: string) => Promise<PasswordResetResult>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;
  getAllUsers: () => Promise<AuthUser[]>;
  getAllRegisteredUsers: () => Promise<RegisteredUser[]>;
  getCurrentUserProfile: () => Promise<User | null>;
  refreshCurrentUserProfile: () => Promise<User | null>;
  invalidateCurrentUserProfile: () => void;
  updateUserProfile: (profileData: Partial<User>) => Promise<boolean>;
  updatePassword: (newPassword: string) => Promise<boolean>;
  checkUserExists: (email: string) => Promise<boolean>;
  exportUsersToJSON: () => Promise<string>;
  isAdmin: () => Promise<boolean>;
  isSuperAdmin: () => Promise<boolean>;
  hasAdminAccess: () => Promise<boolean>;
  hasSuperAdminAccess: () => Promise<boolean>;
  hasOrganizerAccess: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const formatWeakPasswordErrorMessage = (error: unknown): string => {
  if (!isAuthWeakPasswordError(error)) {
    return 'La contraseña no cumple con los requisitos de seguridad.';
  }

  const rawMessage = (error.message || '').trim();
  const reasons = (error.reasons || []).filter(Boolean);
  const normalizedMessage = rawMessage.toLowerCase();

  const looksLikeCharacterPolicyError =
    normalizedMessage.includes('password should contain at least one character of each') ||
    reasons.some((reason: string) => reason.toLowerCase().includes('characters'));

  if (looksLikeCharacterPolicyError) {
    return [
      'La contraseña no cumple con los requisitos de seguridad.',
      'Debe incluir al menos:',
      '- una letra minúscula',
      '- una letra mayúscula',
      '- un número',
      '- un símbolo (por ejemplo: !@#$%^&*)',
    ].join('\n');
  }

  if (!reasons.length) {
    return rawMessage || 'La contraseña no cumple con los requisitos de seguridad.';
  }

  const reasonsText = reasons.map((reason: string) => `- ${reason}`).join('\n');
  return ['La contraseña no cumple con los requisitos de seguridad.', 'Motivos:', reasonsText].join('\n');
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<User | null>(null);
  const profilePromiseRef = useRef<Promise<User | null> | null>(null);

  const clearProfileCache = () => {
    profilePromiseRef.current = null;
    setCurrentUserProfile(null);
  };

  // Helper interno: Verificar y crear perfil si no existe
  const ensureProfileExists = async (
    userId: string,
    email: string,
    metadataName?: string
  ): Promise<boolean> => {
    try {
      const profile = await profilesService.getUserById(userId);
      
      if (profile) {
        // Retrasar actualización de last_login para no bloquear el arranque
        setTimeout(() => {
          profilesService.updateLastLogin(userId).catch(() => {
            // Ignorar errores silenciosamente en actualización de last_login
          });
        }, 2000);
        return true;
      }
      
      // Intentar crear el perfil
      const name = metadataName || generateNameFromEmail(email);
      const profileCreated = await profilesService.createProfile(userId, email, name, '');
      
      if (!profileCreated) {
        logError(
          new Error('No se pudo crear el perfil'),
          'ensureProfileExists',
          true
        );
        return false;
      }
      
      return true;
    } catch (error) {
      logError(error, 'ensureProfileExists', true);
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;
    
    // Función para establecer estado final
    const setFinalState = (session: any) => {
      if (!isMounted) return;
      
      if (session?.user) {
        setIsAuthenticated(true);
        setCurrentUser(session.user.email || null);
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
        clearProfileCache();
      }
      setIsLoading(false);
    };

    // Verificar sesión inicial de forma rápida
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn('Error al obtener sesión:', error);
          setFinalState(null);
          return;
        }

        if (session?.user) {
          // Establecer autenticación inmediatamente
          setIsAuthenticated(true);
          setCurrentUser(session.user.email || null);
          setIsLoading(false);
          
          // Verificar perfil en segundo plano
          ensureProfileExists(
            session.user.id,
            session.user.email || '',
            session.user.user_metadata?.name
          ).catch((error) => {
            logError(error, 'initAuth.ensureProfileExists', true);
          });
        } else {
          setFinalState(null);
        }
      } catch (error) {
        logError(error, 'initAuth', true);
        setFinalState(null);
      }
    };

    // Timeout de seguridad (3 segundos)
    loadingTimeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('Timeout de carga - verificando sesión final');
        supabase.auth.getSession().then(({ data: { session } }) => {
          setFinalState(session);
        });
      }
    }, 3000);

    // Iniciar autenticación
    initAuth();
    
    // Escuchar cambios en la sesión de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      // Limpiar timeout si ya tenemos respuesta
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }

      if (event === 'SIGNED_OUT' || !session) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setIsPasswordRecovery(false);
        clearProfileCache();
        setIsLoading(false);
        return;
      }

      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      
      if (session?.user) {
        const email = session.user.email || '';
        setIsAuthenticated(true);
        setCurrentUser(email);
        setIsLoading(false);
        
        // Verificar perfil en segundo plano
        ensureProfileExists(
          session.user.id,
          email,
          session.user.user_metadata?.name
        ).then((profileExists) => {
          if (!profileExists && isMounted) {
            supabase.auth.signOut();
          }
        }).catch((error) => {
          logError(error, 'onAuthStateChange.ensureProfileExists', true);
        });
      } else {
        setFinalState(null);
      }
    });

    return () => {
      isMounted = false;
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
      subscription.unsubscribe();
    };
  }, []);


  const getAllUsers = async (): Promise<AuthUser[]> => {
    try {
      const profiles = await profilesService.getAllUsers();
      return profiles.map(profile => ({
        email: profile.email,
        password: '', // No almacenamos passwords en Supabase
        createdAt: profile.createdAt,
      }));
    } catch (error) {
      logError(error, 'getAllUsers');
      return [];
    }
  };

  const getAllRegisteredUsers = async (): Promise<RegisteredUser[]> => {
    try {
      const profiles = await profilesService.getAllUsers();
      return profiles.map(profile => ({
        ...profile,
        password: '', // No almacenamos passwords en Supabase
      }));
    } catch (error) {
      logError(error, 'getAllRegisteredUsers');
      return [];
    }
  };

  const checkUserExists = async (email: string): Promise<boolean> => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const profile = await profilesService.getUserByEmail(normalizedEmail);
      return profile !== null;
    } catch (error) {
      logError(error, 'checkUserExists');
      return false;
    }
  };

  const register = async (
    email: string,
    password: string,
    name?: string
  ): Promise<RegisterResult> => {
    // Validación de email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.error };
    }

    // Validación de contraseña
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      return { success: false, error: passwordValidation.error };
    }

    // Verificar si el usuario ya existe
    const normalizedEmail = normalizeEmail(email);
    const userExists = await checkUserExists(normalizedEmail);
    if (userExists) {
      return {
        success: false,
        error: 'Este email ya está registrado. Por favor inicia sesión o usa otro email.',
      };
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password: password.trim(),
        options: {
          data: {
            name: name || generateNameFromEmail(normalizedEmail),
          },
          emailRedirectTo: undefined,
        },
      });

      if (error) {
        const appError = handleSupabaseError(error);
        const errorMessage = appError.message || error.message || '';
        
        // Manejar errores específicos de Supabase
        if (
          errorMessage.includes('already registered') ||
          errorMessage.includes('already exists') ||
          errorMessage.includes('User already registered')
        ) {
          return {
            success: false,
            error: 'Este email ya está registrado. Por favor inicia sesión o usa otro email.',
          };
        }
        
        // Errores de contraseña - detectar de forma fiable
        if (isAuthWeakPasswordError(error)) {
          return { success: false, error: formatWeakPasswordErrorMessage(error) };
        }
        
        // No convertir cualquier error con "email" en "email inválido":
        // Supabase devuelve mensajes como "Error sending confirmation email" que no implican
        // un formato incorrecto, sino configuración SMTP.
        const errorCode = (error as unknown as { code?: string }).code;
        const emailLooksInvalid =
          errorCode === 'email_address_invalid' ||
          /email address.*invalid/i.test(errorMessage) ||
          /email.*invalid/i.test(errorMessage);
        if (emailLooksInvalid) {
          return { success: false, error: 'El formato del email no es válido.' };
        }
        
        // Mostrar el mensaje de error real de Supabase para ayudar a debuggear
        return {
          success: false,
          error: errorMessage || 'Error al registrar el usuario. Por favor intenta de nuevo.',
        };
      }

      if (!data.user) {
        logError(new Error('No se recibió usuario del registro'), 'register', true);
        return {
          success: false,
          error: 'Error al crear el usuario. Por favor intenta de nuevo.',
        };
      }

      // Esperar a que el trigger cree el perfil
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const profile = await profilesService.getUserById(data.user.id);
      
      if (profile) {
        await profilesService.updateProfile(data.user.id, {
          name: name || data.user.user_metadata?.name || generateNameFromEmail(normalizedEmail),
          avatar: '',
        });
      }

      return { success: true };
    } catch (error) {
      const appError = handleSupabaseError(error);
      logError(appError, 'register', true);
      return {
        success: false,
        error: getErrorMessage(appError),
      };
    }
  };

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    // Validación básica
    if (!email.trim() || !password.trim()) {
      return { success: false, error: 'Por favor completa todos los campos' };
    }

    try {
      const normalizedEmail = normalizeEmail(email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: password.trim(),
      });

      if (error) {
        const appError = handleSupabaseError(error);
        
        if (
          appError.message.includes('Email not confirmed') ||
          appError.message.includes('email_not_confirmed')
        ) {
          return {
            success: false,
            error: 'email_not_confirmed',
          };
        }
        
        return {
          success: false,
          error: appError.message || 'Credenciales inválidas',
        };
      }

      if (!data.user) {
        logError(new Error('No se recibió usuario del login'), 'login', true);
        return { success: false, error: 'Error al iniciar sesión' };
      }

      // Verificar y crear perfil si no existe
      await ensureProfileExists(
        data.user.id,
        data.user.email || normalizedEmail,
        data.user.user_metadata?.name
      );

      setIsAuthenticated(true);
      setCurrentUser(data.user.email || normalizedEmail);
      clearProfileCache();
      return { success: true };
    } catch (error) {
      const appError = handleSupabaseError(error);
      logError(appError, 'login', true);
      return {
        success: false,
        error: getErrorMessage(appError),
      };
    }
  };

  const logout = async () => {
    try {
      // Actualizar estado local primero para una respuesta más rápida
      setIsAuthenticated(false);
      setCurrentUser(null);
      clearProfileCache();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // Si hay error, el estado ya se actualizó, pero logueamos el error
        logError(error, 'logout', true);
        // No lanzamos el error para que la UI pueda continuar
      }
    } catch (error) {
      // Aún así, el estado local ya está actualizado
      logError(error, 'logout', true);
      // No lanzamos el error para que la UI pueda continuar
    }
  };

  const deleteAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return {
          success: false,
          error: 'No hay usuario autenticado',
        };
      }

      // Eliminar el perfil primero (esto también limpiará datos relacionados por CASCADE)
      const profileDeleted = await profilesService.deleteProfile(user.id);
      
      if (!profileDeleted) {
        logError(
          new Error('No se pudo eliminar el perfil'),
          'deleteAccount',
          true
        );
        // Continuamos de todas formas para intentar eliminar el usuario de auth
      }

      // Eliminar el usuario de auth.users
      // Nota: En Supabase, esto requiere permisos de administrador o usar una función edge
      // Por ahora, intentamos eliminar el perfil y luego hacer logout
      // El usuario de auth.users se puede eliminar manualmente desde el dashboard o con una función edge
      
      // Actualizar estado local
      setIsAuthenticated(false);
      setCurrentUser(null);
      clearProfileCache();
      
      // Cerrar sesión
      await supabase.auth.signOut();

      return { success: true };
    } catch (error) {
      const appError = handleSupabaseError(error);
      logError(appError, 'deleteAccount', true);
      
      // Aún así, actualizar el estado local
      setIsAuthenticated(false);
      setCurrentUser(null);
      clearProfileCache();
      
      return {
        success: false,
        error: getErrorMessage(appError),
      };
    }
  };

  const loadProfileFromSupabase = async (): Promise<User | null> => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        logError(error, 'AuthContext.loadProfileFromSupabase.getUser', true);
        return currentUserProfile;
      }
      if (!user) {
        clearProfileCache();
        return null;
      }

      const profile = await profilesService.getUserById(user.id);

      if (!profile) {
        logError(
          new Error('Perfil no encontrado para usuario autenticado'),
          'AuthContext.loadProfileFromSupabase',
          true
        );
        await supabase.auth.signOut();
        setIsAuthenticated(false);
        setCurrentUser(null);
        clearProfileCache();
        return null;
      }

      setCurrentUserProfile(profile);
      return profile;
    } catch (error) {
      logError(error, 'AuthContext.loadProfileFromSupabase', true);
      return currentUserProfile;
    }
  };

  const getCurrentUserProfileInternal = async (forceRefresh = false): Promise<User | null> => {
    if (!forceRefresh && currentUserProfile) {
      return currentUserProfile;
    }

    if (profilePromiseRef.current) {
      return profilePromiseRef.current;
    }

    const profilePromise = loadProfileFromSupabase().finally(() => {
      profilePromiseRef.current = null;
    });

    profilePromiseRef.current = profilePromise;
    return profilePromise;
  };

  const getCurrentUserProfile = () => getCurrentUserProfileInternal(false);

  const refreshCurrentUserProfile = () => getCurrentUserProfileInternal(true);

  const invalidateCurrentUserProfile = () => {
    clearProfileCache();
  };

  const updateUserProfile = async (profileData: Partial<User>): Promise<boolean> => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        logError(authError, 'AuthContext.updateUserProfile.auth', true);
        return false;
      }
      if (!user) {
        logError(new Error('No hay usuario autenticado'), 'AuthContext.updateUserProfile.noUser', true);
        return false;
      }
      
      const result = await profilesService.updateProfile(user.id, profileData);
      if (result) {
        setCurrentUserProfile((prev) => (prev ? { ...prev, ...profileData } : prev));
        profilePromiseRef.current = null;
      }
      return result;
    } catch (error) {
      logError(error, 'updateUserProfile', true);
      return false;
    }
  };

  const updatePassword = async (newPassword: string): Promise<boolean> => {
    const result = await completePasswordReset(newPassword);
    return result.success;
  };

  const requestPasswordReset = async (email: string): Promise<PasswordResetResult> => {
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.error };
    }

    try {
      const normalizedEmail = normalizeEmail(email);
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: getPasswordResetRedirectUrl(),
      });

      if (error) {
        logError(error, 'requestPasswordReset', true);
      }

      // Mensaje genérico siempre (evita enumeración de cuentas)
      return { success: true };
    } catch (error) {
      logError(error, 'requestPasswordReset', true);
      return { success: true };
    }
  };

  const completePasswordReset = async (newPassword: string): Promise<PasswordResetResult> => {
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      return { success: false, error: passwordValidation.error };
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword.trim(),
      });

      if (error) {
        if (isAuthWeakPasswordError(error)) {
          return { success: false, error: formatWeakPasswordErrorMessage(error) };
        }
        const appError = handleSupabaseError(error);
        logError(appError, 'completePasswordReset', true);
        return { success: false, error: getErrorMessage(appError) };
      }

      setIsPasswordRecovery(false);
      return { success: true };
    } catch (error) {
      const appError = handleSupabaseError(error);
      logError(appError, 'completePasswordReset', true);
      return { success: false, error: getErrorMessage(appError) };
    }
  };

  const exportUsersToJSON = async (): Promise<string> => {
    try {
      const registeredUsers = await getAllRegisteredUsers();
      const usersToExport = registeredUsers.map(({ password, ...user }) => user);
      return JSON.stringify({ users: usersToExport }, null, 2);
    } catch (error) {
      logError(error, 'exportUsersToJSON');
      return '{}';
    }
  };

  const isAdmin = async (): Promise<boolean> => {
    try {
      const profile = await getCurrentUserProfile();
      return checkIsAdmin(profile);
    } catch (error) {
      logError(error, 'isAdmin');
      return false;
    }
  };

  const isSuperAdmin = async (): Promise<boolean> => {
    try {
      const profile = await getCurrentUserProfile();
      return checkHasSuperAdminAccess(profile);
    } catch (error) {
      logError(error, 'isSuperAdmin');
      return false;
    }
  };

  const hasAdminAccess = async (): Promise<boolean> => {
    try {
      const profile = await getCurrentUserProfile();
      return checkHasAdminAccess(profile);
    } catch (error) {
      logError(error, 'hasAdminAccess');
      return false;
    }
  };

  const hasSuperAdminAccess = async (): Promise<boolean> => {
    try {
      const profile = await getCurrentUserProfile();
      return checkHasSuperAdminAccess(profile);
    } catch (error) {
      logError(error, 'hasSuperAdminAccess');
      return false;
    }
  };

  const hasOrganizerAccess = async (): Promise<boolean> => {
    try {
      const profile = await getCurrentUserProfile();
      if (!profile?.id) return false;
      // Admin de plataforma tambien debe poder entrar al panel de organizer.
      if (checkHasAdminAccess(profile)) return true;
      if (checkHasSuperAdminAccess(profile)) return true;
      return await organizationsService.hasOrgAdminRole(profile.id);
    } catch (error) {
      logError(error, 'hasOrganizerAccess');
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        isPasswordRecovery,
        currentUser,
        currentUserProfile,
        login,
        register,
        requestPasswordReset,
        completePasswordReset,
        logout,
        deleteAccount,
        getAllUsers,
        getAllRegisteredUsers,
        getCurrentUserProfile,
        refreshCurrentUserProfile,
        invalidateCurrentUserProfile,
        updateUserProfile,
        updatePassword,
        checkUserExists,
        exportUsersToJSON,
        isAdmin,
        isSuperAdmin,
        hasAdminAccess,
        hasSuperAdminAccess,
        hasOrganizerAccess,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

